import React from 'react';
import {Button, ButtonProps} from 'reactstrap';
import {Link, LinkProps} from 'react-router-dom';
import ControlledForm from '@src/components/Form/ControlledForm';
import {IEmptyModel} from '@src/@interfaces/IEmptyModel';
import {FormConfigBehavior, FormViewFromProps} from '@src/@types/components/Form';
import {IFormConfigBehaviors} from '@src/@interfaces/components/Form/IFormConfig';
import Translate from '@src/components/Translate/Translate';
import app from '@src/helpers/app';
import NotFoundException from '@src/exceptions/NotFoundException';
import {HTTPMethods} from '@src/@interfaces/http/HTTPMethods';
import {EndpointUri} from '@src/@types/EndpointUri';
import {FormDataProviderAPIContext} from '@src/contexts/FormDataProviderAPIContext';
import {IModelFormConfig} from '@src/@interfaces/components/Form/IModelFormConfig';
import {FormFieldDesignContext} from '@src/contexts/FormFieldDesignContext';

export type Props<Model extends IEmptyModel> = FormViewFromProps<Model> & {
    isReadOnly?: boolean,
    config: IModelFormConfig<Model>,
    formName: string,
    context: string,
    cancelButtonHref?: string,
    showFormUrl: EndpointUri,
    onCancel?: () => void,
    renderAdditionalControls?: (formIsReadOnly: boolean) => React.ReactNode,
};

export enum Events {
    ON_SUBMIT = 'form-view-form.on-submit',
    ON_SUBMIT_SUCCESS = 'form-view-form.on-submit.success',
    ON_SUBMIT_FAIL = 'form-view-form.on-submit.fail',
    ON_SUBMIT_COMPLETE = 'form-view-form.on-submit.complete',
    ON_SUBMIT_DATA_REFRESHED = 'form-view-form.on-submit.data-refreshed',
}

export default class FormViewForm<Model extends IEmptyModel = IEmptyModel> extends ControlledForm<Model, Props<Model>> {
    protected isSubmitAndExitButton = false;

    protected get config() {
        return this.props.config;
    }

    protected get controls() {
        return <div className="mt-5 text-end">
            {this.renderCancelButton()}

            {this.renderAdditionalControls()}

            {this.props.isReadOnly !== true ? <>
                {this.renderSaveAndExitButton()}

                {this.renderSaveButton()}
            </> : null}
        </div>;
    }

    protected get formFieldDesignContextOptions() {
        return {
            fieldsAreReadOnly: this.props.isReadOnly === true,
        };
    }

    protected async onSubmit(data: Record<string, any>): Promise<any> {
        if (this.props.isReadOnly) {
            return Promise.resolve();
        }

        app('events').dispatch(Events.ON_SUBMIT, {
            controllerName: this.props.formName,
        });

        return super.onSubmit(data);
    }

    protected async onSubmitRequestSuccess(res: any): Promise<any> {
        app('events').dispatch(Events.ON_SUBMIT_SUCCESS, {
            controllerName: this.props.formName,
        });

        return super.onSubmitRequestSuccess(res);
    }

    protected async onSubmitRequestFailure(error: Error): Promise<any> {
        app('events').dispatch(Events.ON_SUBMIT_FAIL, {
            controllerName: this.props.formName,
        });

        return super.onSubmitRequestFailure(error);
    }

    protected async onSubmitRequestCompleted(): Promise<void> {
        app('events').dispatch(Events.ON_SUBMIT_COMPLETE, {
            controllerName: this.props.formName,
        });

        return super.onSubmitRequestCompleted();
    }

    protected getBehavior(behaviorName: keyof IFormConfigBehaviors): FormConfigBehavior | null {
        if (behaviorName === 'onSubmit') {
            return this.getOnSubmitBehavior();
        }

        if (behaviorName === 'onSuccess') {
            return this.getOnSuccessBehavior();
        }

        return super.getBehavior(behaviorName);
    }

    render() {
        return <FormDataProviderAPIContext.Provider value={{
            config: this.config,
            formName: this.props.formName,
            context: this.props.context,
            model: this.props.model,
            modelOptions: this.props.modelOptions,
        }}>
            {super.render()}
        </FormDataProviderAPIContext.Provider>;
    }

    protected renderAdditionalControls(): React.ReactNode {
        if (!this.props.renderAdditionalControls) {
            return null;
        }

        return this.props.renderAdditionalControls(this.props.isReadOnly === true);
    }

    protected renderCancelButton(): React.ReactNode {
        if (this.props.onCancel) {
            return <Translate<HTMLButtonElement>
                    message="Forms.cancel_changes"
                    className="btn btn-link"
                    tag="button"
                    type="button"
                    onClick={this.props.onCancel}
            />;
        }

        const href = this.props.cancelButtonHref;
        if (!href) {
            return null;
        }

        return <Translate<HTMLAnchorElement, LinkProps>
                message="Forms.cancel_changes"
                className="btn btn-link"
                tag={Link}
                to={href}
        />;
    }

    protected renderSaveButton(): React.ReactNode {
        return <FormFieldDesignContext.Consumer>{([designContext]) => {
            return <Translate<HTMLButtonElement, ButtonProps>
                    message="Forms.save_changes"
                    tag={Button}
                    type="submit"
                    color="primary"
                    disabled={designContext.fieldsAreDisabled || designContext.fieldsAreReadOnly}
                    onClick={() => {
                        this.isSubmitAndExitButton = false;
                    }}
                    className="ms-2"
            />;
        }}</FormFieldDesignContext.Consumer>;
    }

    protected renderSaveAndExitButton(): React.ReactNode {
        if (!this.props.onCancel && !this.props.cancelButtonHref) {
            return null;
        }

        return <FormFieldDesignContext.Consumer>{([designContext]) => {
            return <Translate<HTMLButtonElement, ButtonProps>
                    message="Forms.save_changes_and_exit"
                    tag={Button}
                    type="submit"
                    color="success"
                    disabled={designContext.fieldsAreDisabled || designContext.fieldsAreReadOnly}
                    onClick={() => {
                        this.isSubmitAndExitButton = true;
                    }}
                    className="ms-2"
            />;
        }}</FormFieldDesignContext.Consumer>;
    }

    protected getOnSubmitBehavior(): FormConfigBehavior | null {
        const context = this.getContextType();

        const namesResolver = app('names-resolver').apiRoutes;
        const apiRouter = app('api-router');

        let updateModelRoute;
        let actionUrl: string | null = null;
        const defaultRouteParams = {id: this.props.model?.id ?? 'create'};

        if (context === 'update' && this.config.updateModelRequestEndpoint) {
            const endpoint = this.config.updateModelRequestEndpoint;
            const route = apiRouter.getRouteByName(typeof endpoint === 'string' ? endpoint : endpoint.route);

            if (route) {
                updateModelRoute = route;
            }

            if (route && typeof endpoint === 'object' && endpoint.params != null) {
                if (Array.isArray(endpoint.params)) {
                    actionUrl = route.resolveUrl([...endpoint.params, defaultRouteParams.id]);
                } else {
                    actionUrl = route.resolveUrl({
                        ...endpoint.params,
                        ...defaultRouteParams,
                    });
                }
            }
        }

        if (!updateModelRoute) {
            let routeNames;
            if (context === 'create') {
                routeNames = [
                    namesResolver.getCreateRouteName(this.props.formName),
                    namesResolver.getCreateRouteName(),
                ];
            } else if (context === 'update') {

                routeNames = [
                    namesResolver.getUpdateRouteName(this.props.formName),
                    namesResolver.getUpdateRouteName(),
                ];
            } else {
                routeNames = [
                    namesResolver.resolveRouteName(this.props.formName, context),
                    namesResolver.resolveRouteName(null, context),
                ];
            }

            for (const name of routeNames) {
                updateModelRoute = apiRouter.getRouteByName(name);
                if (updateModelRoute) {
                    break;
                }
            }
        }

        if (!updateModelRoute) {
            throw new NotFoundException(
                    `Route with name [${namesResolver.getDefaultBaseModelName()}.${context}] not found`
            );
        }

        return {
            type: 'send-request',
            method: updateModelRoute.getMethods().at(0) as HTTPMethods,
            action: actionUrl ?? updateModelRoute.resolveUrl(defaultRouteParams),
            additionalData: {
                'controller-name': this.props.formName,
            }
        };
    }

    protected getOnSuccessBehavior(): FormConfigBehavior | null {
        const shouldRedirectToList = this.isSubmitAndExitButton && (
                this.props.cancelButtonHref ||
                this.props.onCancel
        );

        if (!shouldRedirectToList && !this.isUpdateContext()) {
            return {
                type: 'redirect',
                to: this.props.showFormUrl,
            };
        }

        if (!shouldRedirectToList) {
            if (this.props.refetchModel) {
                this.props.refetchModel().then(data => {
                    app('events').dispatch(Events.ON_SUBMIT_DATA_REFRESHED, {
                        controllerName: this.props.formName,
                        data,
                    });
                });
            }

            return null;
        }

        if (this.props.onCancel) {
            this.props.onCancel();

            return null;
        }

        return {
            type: 'redirect',
            to: this.props.cancelButtonHref as string,
        };
    }

    protected isUpdateContext(): boolean {
        return this.getContextType() === 'update';
    }

    protected getContextType(): string {
        return this.props.context;
    }
}
